// Localização: frontend/src/components/Prontuario/TimelineView.jsx (Versão 1.0.0)
// Componente de visualização de timeline do prontuário

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    ChevronLeft, 
    ChevronRight, 
    ZoomIn, 
    ZoomOut, 
    Calendar,
    Clock,
    User,
    FileText,
    Heart,
    Pill,
    Stethoscope,
    CheckSquare,
    Info
} from 'lucide-react';
import { getTimeline } from '../../api/api';
import LoadingIndicator from '../Common/LoadingIndicator';
import Alert from '../UI/Alert';
import { formatDate, formatDateTime } from '../../utils/formatters';
import TimelineEvent from './TimelineEvent';

// Configuração de cores por tipo de evento
const EVENT_COLORS = {
    registro: {
        bg: 'bg-blue-50',
        border: 'border-blue-400',
        dot: 'bg-blue-500',
        text: 'text-blue-700'
    },
    sinais_vitais: {
        bg: 'bg-red-50',
        border: 'border-red-400',
        dot: 'bg-red-500',
        text: 'text-red-700'
    },
    medicacao: {
        bg: 'bg-purple-50',
        border: 'border-purple-400',
        dot: 'bg-purple-500',
        text: 'text-purple-700'
    },
    equipamento: {
        bg: 'bg-green-50',
        border: 'border-green-400',
        dot: 'bg-green-500',
        text: 'text-green-700'
    },
    tarefa: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-400',
        dot: 'bg-yellow-500',
        text: 'text-yellow-700'
    }
};

// Ícones por tipo de evento
const EVENT_ICONS = {
    registro: FileText,
    sinais_vitais: Heart,
    medicacao: Pill,
    equipamento: Stethoscope,
    tarefa: CheckSquare
};

// Opções de zoom
const ZOOM_OPTIONS = [
    { value: 'day', label: 'Dia', days: 1 },
    { value: 'week', label: 'Semana', days: 7 },
    { value: 'month', label: 'Mês', days: 30 },
    { value: 'year', label: 'Ano', days: 365 }
];

/**
 * Componente de Timeline do Prontuário
 * @param {number} pacienteId - ID do paciente
 * @param {function} onEventClick - Callback quando evento é clicado
 */
const TimelineView = ({ pacienteId, onEventClick }) => {
    const [events, setEvents] = useState([]);
    const [groupedByDate, setGroupedByDate] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zoom, setZoom] = useState('month');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, hasMore: false });

    // Calcular período baseado no zoom
    const getPeriodDates = useCallback((date, zoomLevel) => {
        const zoomOption = ZOOM_OPTIONS.find(z => z.value === zoomLevel) || ZOOM_OPTIONS[2];
        const start = new Date(date);
        const end = new Date(date);
        
        switch (zoomLevel) {
            case 'day':
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'week':
                const dayOfWeek = start.getDay();
                const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Segunda-feira
                start.setDate(diff);
                start.setHours(0, 0, 0, 0);
                end.setDate(diff + 6);
                end.setHours(23, 59, 59, 999);
                break;
            case 'month':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(start.getMonth() + 1);
                end.setDate(0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'year':
                start.setMonth(0, 1);
                start.setHours(0, 0, 0, 0);
                end.setMonth(11, 31);
                end.setHours(23, 59, 59, 999);
                break;
        }
        
        return {
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        };
    }, []);

    // Buscar timeline
    const fetchTimeline = useCallback(async (date, zoomLevel, page = 1) => {
        if (!pacienteId) return;

        setIsLoading(true);
        setError(null);

        try {
            const { startDate, endDate } = getPeriodDates(date, zoomLevel);
            
            const response = await getTimeline(pacienteId, {
                startDate,
                endDate,
                page,
                limit: 50
            });

            if (response.data.success) {
                const data = response.data.data;
                setEvents(data.events || []);
                setGroupedByDate(data.groupedByDate || {});
                setPagination(data.pagination || { page: 1, limit: 50, total: 0, hasMore: false });
            } else {
                setError(response.data.message || 'Erro ao carregar timeline');
            }
        } catch (err) {
            console.error('Erro ao buscar timeline:', err);
            setError(err.response?.data?.message || 'Erro ao conectar com o servidor');
        } finally {
            setIsLoading(false);
        }
    }, [pacienteId, getPeriodDates]);

    // Carregar timeline quando mudar data ou zoom
    useEffect(() => {
        fetchTimeline(currentDate, zoom);
    }, [currentDate, zoom, fetchTimeline]);

    // Navegação temporal
    const navigatePeriod = useCallback((direction) => {
        const newDate = new Date(currentDate);
        const zoomOption = ZOOM_OPTIONS.find(z => z.value === zoom) || ZOOM_OPTIONS[2];
        
        switch (zoom) {
            case 'day':
                newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
                break;
            case 'week':
                newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
                break;
            case 'month':
                newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
                break;
            case 'year':
                newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
                break;
        }
        
        setCurrentDate(newDate);
    }, [currentDate, zoom]);

    // Ir para hoje
    const goToToday = useCallback(() => {
        setCurrentDate(new Date());
    }, []);

    // Formatar período atual para exibição
    const currentPeriodLabel = useMemo(() => {
        const { startDate, endDate } = getPeriodDates(currentDate, zoom);
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        switch (zoom) {
            case 'day':
                return formatDate(start);
            case 'week':
                return `${formatDate(start)} - ${formatDate(end)}`;
            case 'month':
                return start.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
            case 'year':
                return start.getFullYear().toString();
            default:
                return formatDate(start);
        }
    }, [currentDate, zoom, getPeriodDates]);

    // Agrupar eventos por data para visualização
    const eventsByDate = useMemo(() => {
        const grouped = {};
        events.forEach(event => {
            const dateKey = new Date(event.data_hora).toLocaleDateString('pt-BR');
            if (!grouped[dateKey]) {
                grouped[dateKey] = [];
            }
            grouped[dateKey].push(event);
        });
        return grouped;
    }, [events]);

    // Ordenar datas (mais recente primeiro)
    const sortedDates = useMemo(() => {
        return Object.keys(eventsByDate).sort((a, b) => {
            return new Date(b.split('/').reverse().join('-')) - new Date(a.split('/').reverse().join('-'));
        });
    }, [eventsByDate]);

    // Handler para clique em evento
    const handleEventClick = useCallback((event) => {
        setSelectedEvent(event);
        if (onEventClick) {
            onEventClick(event);
        }
    }, [onEventClick]);

    if (isLoading && events.length === 0) {
        return <LoadingIndicator message="Carregando timeline..." />;
    }

    if (error) {
        return <Alert type="error" message={error} />;
    }

    return (
        <div className="w-full min-w-0 overflow-x-hidden">
            {/* Controles de Navegação e Zoom */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4 border border-gray-200 w-full min-w-0 overflow-x-hidden">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full min-w-0">
                    {/* Navegação Temporal */}
                    <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-initial">
                        <button
                            onClick={() => navigatePeriod('prev')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="Período anterior"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg min-w-0 flex-1 sm:min-w-[200px] justify-center">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-semibold text-gray-700">
                                {currentPeriodLabel}
                            </span>
                        </div>
                        
                        <button
                            onClick={() => navigatePeriod('next')}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="Próximo período"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                        </button>
                        
                        <button
                            onClick={goToToday}
                            className="px-3 py-2 text-sm bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                        >
                            Hoje
                        </button>
                    </div>

                    {/* Controles de Zoom */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <ZoomOut className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0">
                            {ZOOM_OPTIONS.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setZoom(option.value)}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                        zoom === option.value
                                            ? 'bg-emerald-500 text-white'
                                            : 'text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <ZoomIn className="w-4 h-4 text-gray-400" />
                    </div>
                </div>

                {/* Indicador de Posição Atual */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            <span>
                                {pagination.total} evento{pagination.total !== 1 ? 's' : ''} no período
                            </span>
                        </div>
                        {pagination.hasMore && (
                            <button
                                onClick={() => fetchTimeline(currentDate, zoom, pagination.page + 1)}
                                className="text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                                Carregar mais...
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Timeline */}
            {events.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                    <p className="text-gray-500">Nenhum evento encontrado para este período.</p>
                </div>
            ) : (
                <div className="relative">
                    {/* Linha vertical da timeline */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>

                    {/* Eventos agrupados por data */}
                    <div className="space-y-8">
                        {sortedDates.map((dateKey, dateIndex) => (
                            <div key={dateKey} className="relative">
                                {/* Cabeçalho da Data */}
                                <div className="flex items-center mb-4">
                                    <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                                        <Calendar className="w-4 h-4 text-emerald-600" />
                                        <span className="font-semibold text-gray-700">{dateKey}</span>
                                        <span className="text-sm text-gray-500">
                                            ({eventsByDate[dateKey].length} evento{eventsByDate[dateKey].length !== 1 ? 's' : ''})
                                        </span>
                                    </div>
                                </div>

                                {/* Eventos do Dia */}
                                <div className="ml-16 space-y-4">
                                    {eventsByDate[dateKey].map((event, eventIndex) => {
                                        const colors = EVENT_COLORS[event.tipo_evento] || EVENT_COLORS.registro;
                                        const Icon = EVENT_ICONS[event.tipo_evento] || FileText;
                                        
                                        return (
                                            <TimelineEvent
                                                key={event.id || `${dateIndex}-${eventIndex}`}
                                                event={event}
                                                colors={colors}
                                                icon={Icon}
                                                onClick={() => handleEventClick(event)}
                                                isSelected={selectedEvent?.id === event.id}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modal de Detalhes (simples, pode ser melhorado) */}
            {selectedEvent && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={() => setSelectedEvent(null)}
                >
                    <div 
                        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {selectedEvent.titulo}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {formatDateTime(selectedEvent.data_hora)}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedEvent(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            <div className="space-y-4">
                                {selectedEvent.descricao && (
                                    <div>
                                        <h4 className="font-semibold text-gray-700 mb-2">Descrição</h4>
                                        <p className="text-gray-600">{selectedEvent.descricao}</p>
                                    </div>
                                )}
                                
                                {selectedEvent.usuario_nome && (
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600">
                                            {selectedEvent.usuario_nome}
                                        </span>
                                    </div>
                                )}
                                
                                {selectedEvent.dados_extras && (
                                    <div>
                                        <h4 className="font-semibold text-gray-700 mb-2">Detalhes</h4>
                                        <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                                            {JSON.stringify(selectedEvent.dados_extras, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelineView;

